import type { StepView } from "@/app/queries";
import { Callout } from "@/ui/Callout";
import { CheckRow } from "@/ui/CheckRow";
import { plural } from "@/lib/plural";
import { metaLine, optionalResources, type Resource, type TickFn } from "./model";

/** «Первоисточник» (S4/S8): необязательные ресурсы с подсказкой, что читать.
 *  Свёрнут по умолчанию и стоит вне гейта — отметки только для себя. */
export function Primary({ view, tick }: { view: StepView; tick: TickFn }) {
  const rs = optionalResources(view);
  if (rs.length === 0) return null;
  const title = rs.length === 1 ? rs[0]!.title : `${rs.length} ${plural(rs.length, "источник", "источника", "источников")}`;
  return (
    <div id="primary" className="scroll-mt-20" data-testid="primary">
      <Callout kind="info" tag="первоисточник" title={title} defaultOpen={false}>
        <p className="m-0 mb-3 font-mono text-[11px] text-ink2">необязательное чтение — гейт его не требует</p>
        <div className="flex flex-col gap-4">
          {rs.map((r) => (
            <PrimaryResource key={r.id} r={r} ticks={view.ticks} tick={tick} />
          ))}
        </div>
      </Callout>
    </div>
  );
}

function PrimaryResource({ r, ticks, tick }: { r: Resource; ticks: StepView["ticks"]; tick: TickFn }) {
  const units = new Set(ticks.units);
  return (
    <div className="flex flex-col gap-2" data-testid="primary-resource">
      <div>
        <div className="text-[14px] font-semibold leading-[1.3] text-ink">
          {r.url ? (
            <a href={r.url} target="_blank" rel="noreferrer" className="text-ink no-underline hover:text-acc">
              {r.title}
            </a>
          ) : (
            r.title
          )}
        </div>
        <div className="mt-1 font-mono text-[11.5px] text-mut">{metaLine(r)}</div>
      </div>
      {r.reading_guide && (
        <div>
          <div className="mb-1 font-mono text-[10.5px] uppercase tracking-[.05em] text-mut">что читать</div>
          <p className="m-0 text-pretty text-[13px] leading-[1.55] text-ink2">{r.reading_guide}</p>
        </div>
      )}
      {r.translation_url && (
        <a href={r.translation_url} target="_blank" rel="noreferrer" className="self-start font-mono text-[12px] text-acc no-underline hover:underline">
          ↗ перевод
        </a>
      )}
      <div className="rounded-sm border border-bd bg-bg2 px-3">
        {r.units.length === 0 ? (
          <CheckRow checked={ticks.resources.includes(r.id)} onChange={(on) => tick({ kind: "resource", key: r.id, on })} label="прочитано" />
        ) : (
          r.units.map((u) => (
            <div key={u.key} className="border-b border-bd last:border-b-0">
              <CheckRow checked={units.has(u.key)} onChange={(on) => tick({ kind: "unit", key: u.key, on })} label={u.topic} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
